<template>
  <section>
    <HeadingSmall>
      {{ $options.title }}
    </HeadingSmall>
    <ul class="jc-clearlist">
      <li
        :class="$style.option"
      >
        <Checkbox
          v-model="enableMaxLoginAttempts"
          :class="$style.checkbox"
          data-test-id="maxLoginAttemptsCheckbox"
        />
        <InputNumberInline
          v-model="maxLoginAttempts"
          data-test-id="maxLoginAttemptsInput"
          :isDisabled="!enableMaxLoginAttempts"
          name="maximum login attempts"
          :required="enableMaxLoginAttempts"
          rules="integer|min_value:1|max:3"
          :textAfter="$options.labels.maxLoginAttempts"
        />
      </li>
      <li
        :class="[$style.option, $style.suboption]"
      >
        <Checkbox
          v-model="enableLockoutTimeInSeconds"
          :class="$style.checkbox"
          data-test-id="enableLockoutTimeInSecondsCheckbox"
          :isDisabled="!enableMaxLoginAttempts"
        />
        <InputNumberInline
          v-model="lockoutTimeInSeconds"
          data-test-id="enableLockoutTimeInSecondsInput"
          :isDisabled="!enableLockoutTimeInSeconds"
          name="lockout time"
          :required="enableLockoutTimeInSeconds"
          rules="between:5,90|integer"
          :textAfter="$options.labels.lockoutTimeInSeconds"
        />
      </li>
      <li
        :class="[$style.option, $style.suboption]"
      >
        <Checkbox
          v-model="enableResetLockoutCounter"
          :class="$style.checkbox"
          data-test-id="enableResetLockoutCounterCheckbox"
          :isDisabled="!enableMaxLoginAttempts"
        />
        <InputNumberInline
          v-model="resetLockoutCounterMinutes"
          data-test-id="enableResetLockoutCounterInput"
          :isDisabled="!enableResetLockoutCounter"
          name="reset lockout counter"
          :required="enableResetLockoutCounter"
          rules="between:5,1440|integer"
          :textAfter="$options.labels.resetLockoutCounter"
        >
          <Link
            :href="$options.kbLink"
            isExternal
            target="_blank"
            text="Learn more"
          />
        </InputNumberInline>
      </li>
    </ul>
    <ModalConfirmChange
      data-test-id="lockoutConfirmChange"
      :itemChanged="activeModal"
      :modalText="modalText"
      :modalType="modalType"
      :showModal="!!activeModal"
      @cancel="handleCancelModal"
      @save="handleContinueModal"
    />
  </section>
</template>

<script>
import {
  Checkbox, HeadingSmall, InputNumberInline, Link,
} from '@jumpcloud/ui-components';
import { SecurityModalTypes } from 'client/settings/Security/SecuritySettingsConstants';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalConfirmChange from 'client/settings/Security/ModalConfirmChange.vue';
import createComputedObjectBinding from 'client/util/createComputedObjectBinding';

export const defaultValues = {
  maxLoginAttempts: 6,
  lockoutTimeInSeconds: 600,
  resetLockoutCounterMinutes: 30,
  enableLockoutTimeInSeconds: false,
  enableResetLockoutCounter: false,
};
const labels = {
  maxLoginAttempts: 'failed password or failed MFA attempts (or a combination of both) until lockout',
  lockoutTimeInSeconds: 'minutes until locked account is automatically unlocked',
  resetLockoutCounter: 'minutes until failed password attempts counter is automatically reset',
};
const title = 'Lockout';

export default {
  name: 'FieldsPasswordLockout',

  components: {
    Checkbox,
    HeadingSmall,
    InputNumberInline,
    ModalConfirmChange,
    Link,
  },

  props: {
    value: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      activeModal: '',
      modalText: [],
      modalType: '',
    };
  },

  computed: {
    enableLockoutTimeInSeconds: {
      get() {
        return this.value.enableLockoutTimeInSeconds;
      },

      set(newValue) {
        const isDisabledEmpty = newValue === false && this.value.lockoutTimeInSeconds === 0;
        const lockoutTimeInSeconds = isDisabledEmpty
          ? defaultValues.lockoutTimeInSeconds
          : this.value.lockoutTimeInSeconds;

        this.$emit('input', {
          ...this.value,
          enableLockoutTimeInSeconds: newValue,
          lockoutTimeInSeconds,
        });
      },
    },

    enableMaxLoginAttempts: {
      get() {
        return this.value.enableMaxLoginAttempts;
      },

      set(newValue) {
        const item = 'enableMaxLoginAttempts';

        this.$emit('input', {
          ...this.value,
          enableMaxLoginAttempts: newValue,
        });

        if (newValue === false) {
          this.activeModal = item;

          this.modalText = [
            "If you turn off lockout, your users' accounts will be at risk. We recommend keeping lockout turned on for security purposes.",
            'Are you sure you want to make this change?',
          ];
          this.modalType = SecurityModalTypes.passwordLockout;
        }
      },
    },

    enableResetLockoutCounter: {
      get() {
        return this.value.enableResetLockoutCounter;
      },

      set(newValue) {
        const isDisabledEmpty = newValue === false && this.value.resetLockoutCounterMinutes === '';
        const resetLockoutCounterMinutes = isDisabledEmpty
          ? defaultValues.resetLockoutCounterMinutes
          : this.value.resetLockoutCounterMinutes;

        this.$emit('input', {
          ...this.value,
          enableResetLockoutCounter: newValue,
          resetLockoutCounterMinutes,
        });
      },
    },

    lockoutTimeInSeconds: {
      get() {
        const lockout = this.value.lockoutTimeInSeconds || 0;
        const lockoutInMinutes = Math.ceil(lockout / 60);
        return lockoutInMinutes;
      },
      set(newValue) {
        this.$emit('input', {
          ...this.value,
          lockoutTimeInSeconds: newValue * 60,
        });
      },
    },

    maxLoginAttempts: {
      ...createComputedObjectBinding('maxLoginAttempts'),
    },

    resetLockoutCounterMinutes: {
      ...createComputedObjectBinding('resetLockoutCounterMinutes'),
    },
  },

  methods: {
    handleCancelModal(itemChanged) {
      this.activeModal = '';
      this.$emit('input', {
        ...this.value,
        [itemChanged]: !this.value[itemChanged],
        enableMaxLoginAttempts: !this.value.enableMaxLoginAttempts,
      });
    },

    handleContinueModal(itemChanged) {
      this.activeModal = '';
      const { maxLoginAttempts } = defaultValues;
      const { resetLockoutCounterMinutes } = defaultValues;
      const { lockoutTimeInSeconds } = defaultValues;
      const { enableLockoutTimeInSeconds } = defaultValues;
      const { enableResetLockoutCounter } = defaultValues;
      this.$emit('input', {
        ...this.value,
        [itemChanged]: this.value[itemChanged],
        enableMaxLoginAttempts: this.value.enableMaxLoginAttempts,
        maxLoginAttempts,
        enableLockoutTimeInSeconds,
        lockoutTimeInSeconds,
        enableResetLockoutCounter,
        resetLockoutCounterMinutes,
      });
    },
  },

  labels,
  kbLink: KbArticleUrls.securitySettingsPassword,
  title,
};
</script>

<style module scoped>
@import '../settings.css';
</style>
